/* eslint-disable prettier/prettier */
import { Menu, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'

import { HiTrash, HiUser } from 'react-icons/hi'
import Button from '../../components/UI/Button/Button'
import Input from '../../components/UI/Input/Input'
import Modal from '../../components/UI/Modal/Modal'
import Pagination from '../../components/UI/Table/Pagination'
import Table from '../../components/UI/Table/Table'
import {
  SelfServedClient,
  SelfServedClientUser,
  useAddUserToClient,
  useRemoveUserFromClient,
  useSelfServedClients,
} from '../../services/queries/useSelfServedClients'

interface ClientUsersModalProps {
  open: boolean
  onClose: () => void
  client: SelfServedClient | null
}

const PAGE_SIZE = 10

const ClientUsersModal = ({ open, onClose, client }: ClientUsersModalProps) => {
  const [showAddUserForm, setShowAddUserForm] = useState(false)
  const [addUserData, setAddUserData] = useState({
    userEmail: '',
    role: 'Owner',
    password: '',
  })
  const [addUserErrors, setAddUserErrors] = useState<Record<string, string>>({})
  const [page, setPage] = useState(1)

  const addUserMutation = useAddUserToClient()
  const removeUserMutation = useRemoveUserFromClient()
  const { data: clients = [] } = useSelfServedClients()

  // Get the latest client data from the fetched clients
  const currentClient = client ? clients.find(c => c.id === client.id) : null

  // Reset form when modal opens or client changes
  useEffect(() => {
    if (open) {
      setAddUserData({
        userEmail: '',
        role: 'Owner',
        password: '',
      })
      setAddUserErrors({})
      setShowAddUserForm(false)
      setPage(1)
    }
  }, [open, currentClient])

  if (!currentClient) return null

  const users = currentClient.users || []
  const total = users.length
  const paginated = users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const validateAddUserForm = () => {
    const newErrors: Record<string, string> = {}

    if (!addUserData.userEmail.trim()) {
      newErrors.userEmail = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(addUserData.userEmail)) {
      newErrors.userEmail = 'Please enter a valid email'
    }

    if (!addUserData.role.trim()) {
      newErrors.role = 'Role is required'
    }

    if (!addUserData.password.trim()) {
      newErrors.password = 'Password is required'
    } else if (addUserData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    setAddUserErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateAddUserForm()) {
      return
    }

    try {
      await addUserMutation.mutateAsync({
        userEmail: addUserData.userEmail,
        selfServedClientId: currentClient.id,
        role: addUserData.role,
        password: addUserData.password,
      })

      setAddUserData({
        userEmail: '',
        role: 'Owner',
        password: '',
      })
      setAddUserErrors({})
      setShowAddUserForm(false)
    } catch (error) {
      console.error('Failed to add user:', error)
    }
  }

  const handleRemoveUser = async (user: SelfServedClientUser) => {
    if (!user.userId) {
      console.error('Cannot remove user: userId is null')
      return
    }

    try {
      await removeUserMutation.mutateAsync({
        userId: user.userId,
        selfServedClientId: currentClient.id,
      })
    } catch (error) {
      console.error('Failed to remove user:', error)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setAddUserData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (addUserErrors[field]) {
      setAddUserErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getRoleBadge = (role: string) => {
    const roleColors = {
      Owner: 'bg-blue-100 text-blue-800',
      Admin: 'bg-green-100 text-green-800',
      User: 'bg-gray-100 text-gray-800',
    }

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          roleColors[role as keyof typeof roleColors] || roleColors.User
        }`}
      >
        {role}
      </span>
    )
  }

  return (
    <Modal
      isOpen={open}
      setIsOpen={onClose}
      type="large"
      height="80vh"
      showCloseIcon
      title={`Manage Users - ${currentClient.name}`}
    >
      <div className="space-y-6">
        {/* Add User Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Add New User</h3>
            <Button type="primary" size="small" onClick={() => setShowAddUserForm(!showAddUserForm)}>
              {/* <HiPlus className="w-4 h-4 mr-2" /> */}
              {showAddUserForm ? 'Cancel' : 'Add User'}
            </Button>
          </div>

          {showAddUserForm && (
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    label=""
                    type="email"
                    value={addUserData.userEmail}
                    width="w-full"
                    onChange={e => handleInputChange('userEmail', e.target.value)}
                    placeholder="user@example.com"
                    classNameInput={addUserErrors.userEmail ? 'border-red-500' : ''}
                  />
                </div>
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    id="role"
                    value={addUserData.role}
                    onChange={e => handleInputChange('role', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      addUserErrors.role ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={addUserMutation.isLoading}
                  >
                    <option value="Owner">Owner</option>
                    <option value="Admin">Admin</option>
                    <option value="User">User</option>
                  </select>
                  {addUserErrors.role && <p className="mt-1 text-sm text-red-600">{addUserErrors.role}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <Input
                  label=""
                  type="password"
                  value={addUserData.password}
                  width="w-full"
                  onChange={e => handleInputChange('password', e.target.value)}
                  placeholder="Enter password"
                  classNameInput={addUserErrors.password ? 'border-red-500' : ''}
                />
                {addUserErrors.password && <p className="mt-1 text-sm text-red-600">{addUserErrors.password}</p>}
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={addUserMutation.isLoading}
                  className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {addUserMutation.isLoading ? 'Adding...' : 'Add User'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Users List */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Current Users ({total})</h3>

          {users.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <HiUser className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No users added to this client yet.</p>
              <p className="text-sm">Use the &quot;Add User&quot; button above to get started.</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table dataCount={paginated.length}>
                <Table.Thead>
                  <Table.Tr className="bg-gray-50 border-b">
                    <Table.Th className="py-3">User</Table.Th>
                    <Table.Th className="py-3">Role</Table.Th>
                    <Table.Th className="py-3">Status</Table.Th>
                    <Table.Th className="py-3">Added On</Table.Th>
                    <Table.Th className="py-3">Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginated.map((user, idx) => (
                    <Table.Tr
                      key={user.id}
                      className={`border-t transition-colors ${
                        idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      } hover:bg-blue-50`}
                    >
                      <Table.Td>
                        <div>
                          <div className="font-medium">{user.user?.email || 'N/A'}</div>
                          <div className="text-sm text-gray-500">
                            {user.user?.firstName && user.user?.lastName
                              ? `${user.user.firstName} ${user.user.lastName}`
                              : 'No name provided'}
                          </div>
                        </div>
                      </Table.Td>
                      <Table.Td>{getRoleBadge(user.role)}</Table.Td>
                      <Table.Td>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            user.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </Table.Td>
                      <Table.Td>{formatDate(user.createdAt)}</Table.Td>
                      <Table.Td className="text-right relative" style={{ overflow: 'visible' }}>
                        <Menu as="div" className="relative inline-block text-left">
                          <div>
                            <Menu.Button className="px-2 py-1 rounded border border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-200 transition">
                              &#x22EE;
                            </Menu.Button>
                          </div>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-xl shadow-2xl py-2 ring-1 ring-black/5 focus:outline-none animate-fade-in z-50">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    className={`flex items-center w-full text-left px-4 py-2 rounded transition ${
                                      user.userId
                                        ? `text-red-600 ${active ? 'bg-red-50 text-red-700' : ''}`
                                        : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                    onClick={() => handleRemoveUser(user)}
                                    disabled={removeUserMutation.isLoading || !user.userId}
                                  >
                                    <HiTrash className="w-4 h-4 mr-2" />
                                    Remove
                                  </button>
                                )}
                              </Menu.Item>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {total > PAGE_SIZE && (
                <div className="flex justify-center p-4 border-t bg-gray-50">
                  <Pagination currentPage={page} totalCount={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ClientUsersModal
